import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { viewExperienceCenterForm, changeExperienceCenterFormStatus } from "../../Redux/Actions/experienceCenterActions";
import toast from "react-hot-toast";
import MainLayout from "../../Layout/mainLayout";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import "./viewUserRequest.css";

const MAP_LIBRARIES = ["places"];
const MAP_CONTAINER_STYLE = { width: "100%", height: "260px", borderRadius: "10px" };
const DEFAULT_CENTER = { lat: 28.6139, lng: 77.209 };

const STATUS_OPTIONS = ["requested", "approved", "ongoing", "completed"];

const ViewExperienceCenterForm = () => {
    const { id } = useParams();
    const dispatch = useDispatch();

    const [formData, setFormData] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("");
    const [statusUpdating, setStatusUpdating] = useState(false);

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: MAP_LIBRARIES,
    });

    const fetchDetails = async () => {
        try {
            setPageLoading(true);
            const res = await dispatch(viewExperienceCenterForm(id));
            if (res && res.statusCode === 200) {
                setFormData(res.data);
                setSelectedStatus(res.data.status || "");
            } else {
                toast.error(res?.message || "Failed to load form details");
            }
        } catch {
            toast.error("Error fetching form details");
        } finally {
            setPageLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [dispatch, id]); // eslint-disable-line

    const handleStatusUpdate = async () => {
        if (!selectedStatus || selectedStatus === formData?.status) return;
        try {
            setStatusUpdating(true);
            await dispatch(changeExperienceCenterFormStatus({ id, status: selectedStatus }));
            setFormData((prev) => ({ ...prev, status: selectedStatus }));
        } catch {
            toast.error("Failed to update status");
        } finally {
            setStatusUpdating(false);
        }
    };

    const coords = formData?.experience_center?.location?.coordinates; // [lng, lat]
    const hasCoords = Array.isArray(coords) && coords.length === 2 && (coords[0] !== 0 || coords[1] !== 0);
    const markerPos = hasCoords ? { lat: coords[1], lng: coords[0] } : null;
    const mapCenter = markerPos || DEFAULT_CENTER;

    if (pageLoading) {
        return (
            <MainLayout>
                <div className="vec-loader-wrap">
                    <div className="vec-loader" />
                    <p className="vec-loader-text">Loading form details…</p>
                </div>
            </MainLayout>
        );
    }

    if (!formData) {
        return (
            <MainLayout>
                <div className="vec-empty-wrap">
                    <div className="vec-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                    </div>
                    <p className="vec-empty-text">No Form Data Found.</p>
                    <Link to="/experience_center_form" className="vec-back-link">← Back to List</Link>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="vec-wrapper">

                {/* Header */}
                <div className="vec-header">
                    <div className="vec-header-left">
                        <Link to="/experience_center" className="vec-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vec-header-divider" />
                        <div>
                            <div className="vec-breadcrumb">Experience Center Form / List / View</div>
                            <h1 className="vec-title">{formData?.experience_center?.title || "Form Details"}</h1>
                        </div>
                    </div>
                </div>

                {/* Hero card */}
                <div className="vec-hero-card">
                    <div className="vec-hero-left">
                        {formData?.experience_center?.cover_image ? (
                            <div className="vec-hero-img-wrap">
                                <img src={formData.experience_center.cover_image} alt={formData.experience_center.title} className="vec-hero-img" />
                            </div>
                        ) : (
                            <div className="vec-hero-avatar">
                                {formData?.experience_center?.title?.charAt(0)?.toUpperCase() || "E"}
                            </div>
                        )}
                        <div>
                            <span className="vec-hero-label">Experience Center</span>
                            <span className="vec-hero-name">{formData?.experience_center?.title || "—"}</span>
                            {formData?.experience_center?.center_code && (
                                <span className="vec-hero-code">{formData.experience_center.center_code}</span>
                            )}
                        </div>
                    </div>
                    <div className="vec-hero-meta">
                        <div className={`vec-status-chip vec-status-chip--${String(formData?.status).toLowerCase()}`}>
                            {formData?.status}
                        </div>
                        {formData?.city?.city_name && (
                            <div className="vec-hero-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                {formData.city.city_name}
                            </div>
                        )}
                        <div className="vec-hero-meta-item">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            {formData?.created_at
                                ? new Date(formData.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                                : "—"}
                        </div>
                    </div>
                </div>

                {/* Content grid */}
                <div className="vec-grid-layout">

                    {/* Left column */}
                    <div className="vec-col">

                        {/* User Info */}
                        <div className="vec-card">
                            <div className="vec-card-header">
                                <span className="vec-card-badge">01</span>
                                <h2 className="vec-card-title">User Information</h2>
                            </div>
                            <div className="vec-info-grid">
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Name</span>
                                    <span className="vec-info-value">{formData?.user?.name || "No Data"}</span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Email</span>
                                    <span className="vec-info-value">{formData?.user?.email || "No Data"}</span>
                                </div>
                                <div className="vec-info-item vec-info-item--full">
                                    <span className="vec-info-label">User Input / Request</span>
                                    <span className="vec-info-value">{formData?.user_input || "No Data"}</span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Submitted On</span>
                                    <span className="vec-info-value">
                                        {formData?.created_at
                                            ? new Date(formData.created_at).toLocaleString("en-IN", {
                                                day: "2-digit", month: "short", year: "numeric",
                                                hour: "2-digit", minute: "2-digit",
                                            })
                                            : "No Data"}
                                    </span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">City</span>
                                    <span className="vec-info-value">{formData?.city?.city_name || "No Data"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Status Management */}
                        <div className="vec-card">
                            <div className="vec-card-header">
                                <span className="vec-card-badge">02</span>
                                <h2 className="vec-card-title">Status Management</h2>
                            </div>
                            <div className="vec-status-panel">
                                <div className="vec-status-field">
                                    <span className="vec-info-label">Current Status</span>
                                    <div className={`vec-status-chip vec-status-chip--${String(formData?.status).toLowerCase()}`} style={{ marginTop: 4, alignSelf: "flex-start" }}>
                                        {formData?.status}
                                    </div>
                                </div>
                                <div className="vec-status-field">
                                    <span className="vec-info-label">Update Status</span>
                                    <select
                                        className="vec-status-select"
                                        value={selectedStatus}
                                        onChange={(e) => setSelectedStatus(e.target.value)}
                                    >
                                        {STATUS_OPTIONS.map((s) => (
                                            <option key={s} value={s}>
                                                {s.charAt(0).toUpperCase() + s.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <button
                                    className="vec-status-btn"
                                    onClick={handleStatusUpdate}
                                    disabled={statusUpdating || selectedStatus === formData?.status}
                                >
                                    {statusUpdating ? "Updating…" : "Update Status"}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right column */}
                    <div className="vec-col">

                        {/* Experience Center Info */}
                        <div className="vec-card">
                            <div className="vec-card-header">
                                <span className="vec-card-badge">03</span>
                                <h2 className="vec-card-title">Experience Center Details</h2>
                            </div>
                            <div className="vec-info-grid">
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Title</span>
                                    <span className="vec-info-value">{formData?.experience_center?.title || "No Data"}</span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Center Code</span>
                                    <span className="vec-info-value">{formData?.experience_center?.center_code || "No Data"}</span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Open Time</span>
                                    <span className="vec-info-value">
                                        {formData?.experience_center?.open_timing
                                            ? new Date(formData.experience_center.open_timing).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                            : "No Data"}
                                    </span>
                                </div>
                                <div className="vec-info-item">
                                    <span className="vec-info-label">Close Time</span>
                                    <span className="vec-info-value">
                                        {formData?.experience_center?.close_timing
                                            ? new Date(formData.experience_center.close_timing).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                            : "No Data"}
                                    </span>
                                </div>
                                <div className="vec-info-item vec-info-item--full">
                                    <span className="vec-info-label">Address</span>
                                    <span className="vec-info-value">{formData?.experience_center?.address || "No Data"}</span>
                                </div>
                                {formData?.experience_center?.description && (
                                    <div className="vec-info-item vec-info-item--full">
                                        <span className="vec-info-label">Description</span>
                                        <span className="vec-info-value">{formData.experience_center.description}</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Location Map */}
                        <div className="vec-card">
                            <div className="vec-card-header">
                                <span className="vec-card-badge">04</span>
                                <h2 className="vec-card-title">Location</h2>
                            </div>

                            {hasCoords && (
                                <div className="vec-coords-row">
                                    <div className="vec-coord-chip">
                                        <span className="vec-coord-label">LAT</span>
                                        <span className="vec-coord-value">{coords[1].toFixed(6)}</span>
                                    </div>
                                    <div className="vec-coord-chip">
                                        <span className="vec-coord-label">LNG</span>
                                        <span className="vec-coord-value">{coords[0].toFixed(6)}</span>
                                    </div>
                                </div>
                            )}

                            {!hasCoords && (
                                <div className="vec-map-empty">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                    </svg>
                                    <p>No location set for this center.</p>
                                </div>
                            )}

                            {hasCoords && loadError && (
                                <div className="vec-map-error">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    <p>Map failed to load.</p>
                                </div>
                            )}

                            {hasCoords && !isLoaded && !loadError && (
                                <div className="vec-map-loading">
                                    <div className="vec-loader" />
                                    <p>Loading map…</p>
                                </div>
                            )}

                            {hasCoords && isLoaded && !loadError && (
                                <GoogleMap
                                    mapContainerStyle={MAP_CONTAINER_STYLE}
                                    center={mapCenter}
                                    zoom={14}
                                    options={{
                                        streetViewControl: false,
                                        mapTypeControl: false,
                                        fullscreenControl: false,
                                        zoomControl: true,
                                        scrollwheel: false,
                                    }}
                                >
                                    <Marker position={markerPos} />
                                </GoogleMap>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ViewExperienceCenterForm;